export const INTRO_ELIGIBILITY_ELIGIBLE = 2

function revenueCatError (message, code, cause = undefined) {
  const error = new Error(message)
  error.code = code
  if (cause !== undefined) error.cause = cause
  return error
}

function packageForType (offering, property, packageType) {
  return offering?.[property]
    ?? offering?.availablePackages?.find(pkg => pkg?.packageType === packageType)
    ?? null
}

function hasDisplayableProduct (pkg) {
  return typeof pkg?.identifier === 'string'
    && pkg.identifier.trim() !== ''
    && typeof pkg?.product?.identifier === 'string'
    && pkg.product.identifier.trim() !== ''
    && typeof pkg.product.priceString === 'string'
    && pkg.product.priceString.trim() !== ''
}

function androidIntroIsAvailable (pkg) {
  const option = pkg?.product?.defaultOption
  return !!(option?.freePhase || option?.introPhase)
}

/**
 * RevenueCat-backed current-offering loader with injectable SDK calls for unit tests.
 * Unknown or failed iOS eligibility is deliberately treated as ineligible, because
 * showing introductory terms that the App Store may not grant is misleading.
 */
export function createRevenueCatOfferingLoader ({
  loadOfferings,
  checkIntroEligibility,
  getPlatform,
}) {
  return async function loadCurrentOfferingPackages () {
    let offerings
    try {
      offerings = await loadOfferings()
    } catch (cause) {
      throw revenueCatError('Failed to load RevenueCat offerings', 'RC_OFFERINGS_FAILED', cause)
    }

    const offering = offerings?.current
    const monthly = packageForType(offering, 'monthly', 'MONTHLY')
    const annual = packageForType(offering, 'annual', 'ANNUAL')
    if (!hasDisplayableProduct(monthly) || !hasDisplayableProduct(annual)) {
      throw revenueCatError('Current RevenueCat offering is incomplete', 'RC_PACKAGES_INCOMPLETE')
    }

    const introEligibility = { monthly: false, annual: false }
    const platform = getPlatform()

    if (platform === 'android') {
      introEligibility.monthly = androidIntroIsAvailable(monthly)
      introEligibility.annual = androidIntroIsAvailable(annual)
    } else if (platform === 'ios') {
      const packagesWithIntro = { monthly, annual }
      const productIdentifiers = Object.values(packagesWithIntro)
        .filter(pkg => pkg.product.introPrice)
        .map(pkg => pkg.product.identifier)

      if (productIdentifiers.length > 0) {
        try {
          const statuses = await checkIntroEligibility({ productIdentifiers })
          introEligibility.monthly = statuses?.[monthly.product.identifier]?.status === INTRO_ELIGIBILITY_ELIGIBLE
          introEligibility.annual = statuses?.[annual.product.identifier]?.status === INTRO_ELIGIBILITY_ELIGIBLE
        } catch {
          // Prices remain usable, but introductory wording stays hidden.
        }
      }
    }

    return { offeringIdentifier: offering.identifier, monthly, annual, introEligibility }
  }
}
